import * as css from 'css/exports.module.scss';

import './NotificationsMenu.scss';

import { BellOutlined } from '@ant-design/icons';
import classNames from 'classnames';
import { count } from '@sonar-software/sonar-ts-lib';
import { When } from 'react-if';

import { Badge, Col, Dropdown, Row } from 'antd';
import { ClearAllNotifications, NotificationCards } from '../notifications';
import { notificationsActions, SonarNotification, SonarNotificationType } from '@sonar-software/sonar-notifications';
import React, { useCallback, useEffect, useMemo } from 'react';
import {
	useAction,
	useInterval,
	useNotifications,
	useOptimisticUpdate,
	usePrevious
} from '@hooks';

const badgeOffset: [number, number] = [ -6, 6 ];
const triggerEvents: ['click'] = [ 'click' ];

function isNotificationUnread(notification: SonarNotification): boolean {
	return !notification.isRead;
}

export const NotificationsMenu: React.FC = () => {
	const notifications = useNotifications();
	const previousNotifications = usePrevious(notifications);

	const loadNotifications = useAction(notificationsActions.requestGetNotifications);
	const markAllRead = useAction(notificationsActions.requestPatchNotifications);
	const { clear } = useOptimisticUpdate();

	const refreshNotifications = useCallback(() => {
		loadNotifications();
	}, [ loadNotifications ]);

	useInterval(refreshNotifications, 30000);

	const hasNotifications = Boolean(notifications.length);
	const unreadCount = count(notifications ?? [], isNotificationUnread);

	const overlayClassNames = classNames('notifications-menu', { empty: !hasNotifications });

	const handleChange = useCallback(
		(visible: boolean) => {
			if (visible && unreadCount) {
				markAllRead();
			}
		},
		[ unreadCount, markAllRead ]
	);

	const newNotifications = useMemo(
		() => notifications.slice(0, notifications.length - (previousNotifications?.length ?? 0)),
		[ notifications, previousNotifications?.length ]
	);

	useEffect(() => {
		if (newNotifications.some(
			(notification) => notification.notificationType === SonarNotificationType.SalesforceBulkPatchSummaryReady
		)) {
			clear();
		}
	}, [ clear, newNotifications ]);

	return (
		<Dropdown
			destroyOnHidden
			onOpenChange={ handleChange }
			overlayClassName={ overlayClassNames }
			popupRender={ () =>
				<>
					<When condition={ hasNotifications }>
						<Row
							align='middle'
							className='notification-menu-actions'
							justify='end'>
							<Col span='12' />
							<Col
								className='align-right'
								span='12'>
								<ClearAllNotifications id='notification-menu-clear-all-button' />
							</Col>
						</Row>
					</When>
					<div className='notification-menu-list'>
						<NotificationCards
							height={ window.innerHeight - 100 }
							wrapEmpty />
					</div>
				</> }
			trigger={ triggerEvents }>
			<Badge
				className='notifications-menu-icon'
				color={ css.badgeColor }
				count={ unreadCount }
				offset={ badgeOffset }
				overflowCount={ 99 }>
				<BellOutlined id='notifications-menu-icon' />
			</Badge>
		</Dropdown>
	);
};
